import { ApiError, type ApiErrorBody, http } from './api'
import type { PayoutComment, PayoutOverviewGroup, PayoutOverviewStatus } from './invoice-types'

async function requestVoid(method: string, path: string, body?: FormData): Promise<void> {
  const res = await fetch(path, { method, credentials: 'include', body })
  if (res.ok || res.status === 204) return
  const fallback: ApiErrorBody = { error: { code: `http_${res.status}`, message: res.statusText || 'Onbekende fout' } }
  const parsed = res.headers.get('content-type')?.includes('json') ? await res.json().catch(() => fallback) : fallback
  throw new ApiError(res.status, parsed as ApiErrorBody)
}

export function incomingInvoiceUrl(lineId: string): string {
  return `/api/payouts/lines/${lineId}/incoming-invoice`
}

export const payoutApi = {
  overview(status: PayoutOverviewStatus) {
    return http.get<PayoutOverviewGroup[]>('/api/payouts/overview', { status })
  },

  uploadIncomingInvoice(
    lineId: string,
    fields: {
      file: File
      incomingInvoiceNumber: string
      incomingInvoiceAmount: string
      incomingInvoiceTotal: string
      incomingInvoiceDifferenceReason?: string
    },
  ) {
    const form = new FormData()
    form.append('file', fields.file)
    form.append('incomingInvoiceNumber', fields.incomingInvoiceNumber.trim())
    form.append('incomingInvoiceAmount', fields.incomingInvoiceAmount.trim())
    form.append('incomingInvoiceTotal', fields.incomingInvoiceTotal.trim())
    if (fields.incomingInvoiceDifferenceReason) {
      form.append('incomingInvoiceDifferenceReason', fields.incomingInvoiceDifferenceReason.trim())
    }
    return requestVoid('POST', incomingInvoiceUrl(lineId), form)
  },

  removeIncomingInvoice(lineId: string) {
    return requestVoid('DELETE', incomingInvoiceUrl(lineId))
  },

  approveIncomingInvoice(lineId: string, approve: boolean) {
    return http.post<unknown>(`/api/payouts/lines/${lineId}/approve-invoice`, { approve }).then(() => undefined)
  },

  declineIncomingInvoice(lineId: string, reason: string) {
    return http.post<unknown>(`/api/payouts/lines/${lineId}/decline-invoice`, { reason }).then(() => undefined)
  },

  undoDeclineIncomingInvoice(lineId: string) {
    return http.del<unknown>(`/api/payouts/lines/${lineId}/decline-invoice`).then(() => undefined)
  },

  comments(lineId: string) {
    return http.get<{ comments: PayoutComment[] }>(`/api/payouts/lines/${lineId}/comments`).then((r) => r.comments)
  },

  addComment(lineId: string, text: string) {
    return http.post<{ comments: PayoutComment[] }>(`/api/payouts/lines/${lineId}/comments`, { text }).then((r) => r.comments)
  },

  updateComment(lineId: string, commentId: string, text: string) {
    return http
      .patch<{ comments: PayoutComment[] }>(`/api/payouts/lines/${lineId}/comments/${commentId}`, { text })
      .then((r) => r.comments)
  },

  deleteComment(lineId: string, commentId: string) {
    return http.del<{ comments: PayoutComment[] }>(`/api/payouts/lines/${lineId}/comments/${commentId}`).then((r) => r.comments)
  },
}
